import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Mail, Phone, Clock, XCircle } from 'lucide-react'
import DownloadConnexaButton from '@/components/DownloadConnexaButton'
import PageShell from '@/components/PageShell'
import ProfileHeader from '@/components/ProfileHeader'
import Spinner from '@/components/Spinner'
import StateMessage from '@/components/StateMessage'
import { getProfileAccess } from '@/services/api'
import { logger } from '@/utils/logger'
import { STRINGS } from '@/constants'
import type { BusinessProfile } from '@/types'

const CTX = 'ProfileAccessPage'

type PageState = 'loading' | 'valid' | 'expired' | 'invalid'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function buildHeadline(
  title?: string | null,
  company?: string | null,
): string | null {
  if (title && company) return `${title} at ${company}`
  return title || company || null
}

export default function ProfileAccessPage() {
  const { accessToken = '' } = useParams<{ accessToken: string }>()
  const [state, setState] = useState<PageState>('loading')
  const [profile, setProfile] = useState<BusinessProfile | null>(null)

  useEffect(() => {
    logger.info(CTX, 'mount', { accessToken })
    getProfileAccess(accessToken)
      .then((result) => {
        logger.info(CTX, 'profile access result', result)
        if (!result.valid) {
          setState('invalid')
        } else if (result.expired) {
          if (result.owner) setProfile(result.owner)
          setState('expired')
        } else if (result.owner) {
          setProfile(result.owner)
          setState('valid')
        } else {
          setState('invalid')
        }
      })
      .catch((err) => {
        logger.error(CTX, 'profile access fetch failed', err)
        setState('invalid')
      })
  }, [accessToken])

  if (state === 'loading') {
    return (
      <PageShell>
        <Spinner label={STRINGS.profile.loading} />
      </PageShell>
    )
  }

  if (state === 'expired') {
    return (
      <PageShell>
        <StateMessage
          tone="amber"
          icon={<Clock className="w-7 h-7" strokeWidth={1.5} />}
          title={STRINGS.profile.expired}
          description={STRINGS.profile.expiredDesc}
        />
      </PageShell>
    )
  }

  if (state === 'invalid') {
    return (
      <PageShell>
        <StateMessage
          tone="gray"
          icon={<XCircle className="w-7 h-7" strokeWidth={1.5} />}
          title={STRINGS.profile.invalid}
        />
      </PageShell>
    )
  }

  // Valid state
  const {
    firstName,
    lastName,
    picture,
    businessEmail,
    businessPhone,
    company,
    title,
    accessExpiresAt,
  } = profile!

  return (
    <PageShell>
      <div className="page-content">
        <div className="card flex flex-col items-center gap-6">
          <ProfileHeader
            firstName={firstName}
            lastName={lastName}
            picture={picture}
            headline={buildHeadline(title, company)}
          />

          <div className="w-full flex flex-col gap-3">
            <ContactRow
              icon={<Mail className="w-4 h-4" />}
              label={businessEmail}
              href={`mailto:${businessEmail}`}
            />
            {businessPhone && (
              <ContactRow
                icon={<Phone className="w-4 h-4" />}
                label={businessPhone}
                href={`tel:${businessPhone}`}
              />
            )}
          </div>

          {accessExpiresAt && (
            <p className="text-xs text-gray-400 text-center">
              {STRINGS.profile.availableUntil} {formatDate(accessExpiresAt)}
            </p>
          )}
        </div>

        <div className="px-4">
          <DownloadConnexaButton accessToken={accessToken} />
        </div>
      </div>
    </PageShell>
  )
}

interface ContactRowProps {
  icon: React.ReactNode
  label: string
  href?: string
}

function ContactRow({ icon, label, href }: ContactRowProps) {
  const inner = (
    <>
      <span className="contact-row-icon">{icon}</span>
      <span className="contact-row-label">{label}</span>
    </>
  )

  if (href) {
    return (
      <a
        href={href}
        className="contact-row text-navy hover:text-action transition-colors"
      >
        {inner}
      </a>
    )
  }

  return <div className="contact-row text-gray-600">{inner}</div>
}
